import type { UserRole } from '@cult/domain';
import { useState } from 'react';
import { type UserSearchOption, UserSearchSelect } from '../../../../../components';
import { useDebouncedValue } from '../../../../../lib/use-debounced-value';
import { useAdminUsersInfinite } from '../../hooks';

type Props = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  extraOption?: UserSearchOption | null;
  // Restrict the searchable users to one or more roles (e.g. moderators for praise authors).
  roles?: UserRole[];
  // By default only active users are listed; set to also include deactivated users.
  includeInactive?: boolean;
  style?: React.CSSProperties;
};

// Moderator-side teammate picker: same Select as the member one, backed by the admin user list so
// it can filter by role and reach deactivated accounts.
export const UserSelect = ({ roles, includeInactive, ...rest }: Props) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const usersQuery = useAdminUsersInfinite({
    isActive: includeInactive ? undefined : true,
    role: roles,
    q: debouncedSearch || undefined,
    limit: 10,
  });

  return (
    <UserSearchSelect
      {...rest}
      users={usersQuery.data?.pages.flatMap((p) => p.users) ?? []}
      loading={usersQuery.isFetching}
      canLoadMore={Boolean(usersQuery.hasNextPage) && !usersQuery.isFetchingNextPage}
      onSearch={setSearch}
      onLoadMore={() => void usersQuery.fetchNextPage()}
    />
  );
};
